import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '@/shared/ui/brand-mark';
import { LegalLinks } from '@/shared/ui/legal-links';

export interface AuthFrameProps {
  children: ReactNode;
}

/**
 * Frame for the secondary auth pages (forgot, reset). Same dark portal look as the landing
 * page, one column, card centred. The `dark` class scopes the dark tokens to this tree.
 */
export function AuthFrame({ children }: AuthFrameProps) {
  const { t } = useTranslation('auth');
  return (
    <div className="dark bg-background text-foreground flex min-h-svh flex-col">
      <header className="flex items-center gap-3 p-6 sm:px-10">
        <Link to="/" className="flex items-center gap-3">
          <BrandMark />
          <span className="text-lg font-semibold tracking-tight">{t('landing.brand')}</span>
        </Link>
        <span className="text-muted-foreground border-l pl-3 text-[13px]">
          {t('landing.surface')}
        </span>
      </header>
      <main className="flex flex-1 items-center justify-center p-6 sm:p-10">{children}</main>
      <footer className="text-muted-foreground flex flex-col items-center gap-3 p-6 text-xs sm:px-10">
        <Link to="/login" className="hover:text-foreground hover:underline">
          {t('frame.backToSignIn')}
        </Link>
        <LegalLinks className="[&_a:hover]:text-foreground justify-center [&_a:hover]:underline" />
      </footer>
    </div>
  );
}

/** The card the auth forms sit in; shared so the three pages line up pixel for pixel. */
export function AuthCard({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className="bg-card text-card-foreground w-full max-w-sm rounded-2xl border p-7 shadow-[0_12px_40px_rgba(0,0,0,0.35)] sm:p-9"
    >
      <div className="mb-5 space-y-1.5">
        <h1 id={id} className="text-[22px] font-semibold tracking-tight">
          {title}
        </h1>
        {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}
